/**
 * Shared outlet data for the guaranteed placements marketing flow.
 * Used by both the GuaranteedPlacements grid page and the OutletDetail
 * landing page so the outlet list stays in sync.
 *
 * Prices are NOT hardcoded here — they are pulled live from the
 * guaranteed-placements pricing data (Package entity) via getOutletPrices.
 */

const LOGO_BASE = "https://media.base44.com/images/public/6a460432963500ce22897197/";
const WIKIMEDIA = "https://commons.wikimedia.org/wiki/Special:FilePath/";

export const OUTLETS = [
  { name: "Yahoo Finance", logo: LOGO_BASE + "531e0fb21_Yahoo.svg", colorLogo: true, sector: "Finance", region: "US", da: "95", tier: "Tier 1 Publication" },
  { name: "MarketWatch", logo: LOGO_BASE + "559e66bb3_MarketWatch.svg", colorLogo: true, sector: "Finance", region: "US", da: "92", tier: "Tier 1 Publication" },
  { name: "Benzinga", logo: LOGO_BASE + "28715b015_Benzinga.svg", colorLogo: true, sector: "Finance", region: "US", da: "89", tier: "Tier 1 Publication" },
  { name: "Digital Journal", logo: LOGO_BASE + "8446ced27_DigitalJournal.svg", colorLogo: true, sector: "News", region: "Global", da: "87", tier: null },
  { name: "Gulf News", logo: LOGO_BASE + "23eb8ad36_Gulf_News_idd14Ts0hh_1.svg", sector: "News", region: "MENA", da: "85", tier: "Tier 1 Publication" },
  { name: "Arabian Business", logo: LOGO_BASE + "dce755e42_ArabianBusiness.svg", colorLogo: true, sector: "Business", region: "MENA", da: "78", tier: "Tier 1 Publication" },
  { name: "CoinDesk", logo: LOGO_BASE + "02c19fe77_CoinDesk.svg", colorLogo: true, sector: "Crypto", region: "Global", da: "90", tier: "Tier 1 Publication" },
  { name: "Business Insider", logo: LOGO_BASE + "4c3df51c1_Business_Insider_Logo_0.svg", sector: "Business", region: "US", da: "93", tier: "Tier 1 Publication" },
  { name: "TechCrunch", logo: LOGO_BASE + "780bcbb40_TechCrunch.svg", colorLogo: true, sector: "Tech", region: "Global", da: "92", tier: "Tier 1 Publication" },
  { name: "Rolling Stone UK", logo: LOGO_BASE + "7a94b7eaf_RollingStoneUK.svg", colorLogo: true, sector: "Lifestyle", region: "Global", da: "61", tier: null },
  { name: "Reuters", logo: LOGO_BASE + "3a2fa67ff_Reuters.svg", colorLogo: true, sector: "News", region: "Global", da: "94", tier: "Tier 1 Publication" },
  { name: "MSN", logo: LOGO_BASE + "661b7326c_MSN.svg", colorLogo: true, scale: 1.6, sector: "News", region: "Global", da: "94", tier: "Tier 1 Publication" },
  { name: "Entrepreneur ME", logo: "https://base44.app/api/apps/6a460432963500ce22897197/files/mp/public/6a460432963500ce22897197/a35a4b965_entrepreneur-me-logo-transparent.png", colorLogo: true, scale: 1.3, sector: "Business", region: "MENA", da: "80", tier: "Tier 1 Publication" },
];

/** Convert an outlet name to a URL-friendly slug. */
export function slugifyOutlet(name) {
  return (name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Find an outlet by its URL slug. Returns undefined if not found. */
export function outletBySlug(slug) {
  return OUTLETS.find((o) => slugifyOutlet(o.name) === slug);
}

/** Resolve a static logo URL for an outlet name from the OUTLETS list,
 *  matching case-insensitively and ignoring punctuation / extra whitespace.
 *  Returns the logo URL string, or undefined if no static entry matches. */
export function staticLogoForName(name) {
  let key = slugifyOutlet(name);
  if (!key) return undefined;
  // Exclusion: "Arabian Business" never resolves from the static OUTLETS list —
  // it always falls through to the database record's brand_logo_url.
  if (key === slugifyOutlet("Arabian Business")) return undefined;
  // Alias map applied before the lookup — maps the normalized form of one
  // outlet name to the normalized form of another so either resolves to the
  // same static entry. Currently: "Entrepreneur Middle East" -> "Entrepreneur ME".
  const ALIASES = {
    [slugifyOutlet("Entrepreneur Middle East")]: slugifyOutlet("Entrepreneur ME"),
  };
  if (ALIASES[key]) key = ALIASES[key];
  return OUTLETS.find((o) => slugifyOutlet(o.name) === key)?.logo;
}